import PropTypes from 'prop-types';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';

const imgPrevArrow = 'https://www.figma.com/api/mcp/asset/c929e077-fe92-4c4b-b427-a61171f97508';
const imgNextArrow = 'https://www.figma.com/api/mcp/asset/0d557174-e089-40ed-84c1-9ddc769ce459';

export interface GalleryItem {
  image: string;
  title: string;
  description: string;
  hasRibbon?: boolean;
  onPrevClick?: () => void;
  onNextClick?: () => void;
}

export interface GalleryProps {
  className?: string;
  heading?: string;
  items: GalleryItem[];
}

const GalleryCard = ({ item, isMobile }: { item: GalleryItem; isMobile: boolean }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: globalColours['$global-colour-background-elevated'],
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Image */}
      <Box
        sx={{
          height: '320px',
          width: '100%',
          position: 'relative',
        }}
      >
        <Box
          component='img'
          alt=''
          src={item.image}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Coming Soon Ribbon */}
      {item.hasRibbon && (
        <Box
          sx={{
            position: 'absolute',
            left: isMobile ? '185.98px' : '260.48px',
            top: '-0.48px',
            width: '150px',
            height: '150px',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: '-3.03px',
              top: '-47.18px',
              width: '169.706px',
              height: '169.706px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                transform: 'rotate(45deg)',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  width: '200px',
                  height: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Gill Sans, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '22px',
                      color: '#030213',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Coming Soon
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Text Overlay */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: '216px',
          width: isMobile ? '335px' : '410.33px',
          height: '104px',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.7) 50%, rgba(0,0,0,0))',
          display: 'flex',
          flexDirection: 'column',
          pt: isMobile ? '12px' : '24px',
          px: isMobile ? '12px' : '24px',
        }}
      >
        <Box sx={{ height: '34px', width: '100%', position: 'relative' }}>
          <Typography
            variant='subtitle4'
            sx={{
              position: 'absolute',
              left: 0,
              top: '3px',
              color: globalColours['$global-colour-base-contrast'],
              whiteSpace: 'nowrap',
            }}
          >
            {item.title}
          </Typography>
        </Box>
        <Box sx={{ height: '22px', width: '100%', position: 'relative' }}>
          <Typography
            variant='body3SemiBold'
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              color: globalColours['$global-colour-base-contrast'],
              whiteSpace: 'nowrap',
            }}
          >
            {item.description}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Arrows */}
      {(item.onPrevClick || item.onNextClick) && (
        <>
          {item.onPrevClick && (
            <Box
              onClick={item.onPrevClick}
              sx={{
                position: 'absolute',
                left: isMobile ? '16px' : '15.5px',
                top: '139px',
                width: '48px',
                height: '48px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                boxShadow: '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                component='img'
                alt=''
                src={imgPrevArrow}
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </Box>
          )}
          {item.onNextClick && (
            <Box
              onClick={item.onNextClick}
              sx={{
                position: 'absolute',
                left: isMobile ? '271.66px' : '347.16px',
                top: '139px',
                width: '48px',
                height: '48px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                boxShadow: '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                component='img'
                alt=''
                src={imgNextArrow}
                sx={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const Gallery = ({ className, heading, items }: GalleryProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: globalColours['$global-colour-background-screen-default'],
        width: '100%',
      }}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'center',
          px: { xs: 2.5, md: 0 },
          py: { xs: 0, md: 0 },
        }}
      >
        {/* Heading */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '120px',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              py: 2,
              borderRadius: '16px',
              flex: { xs: '1 0 0', md: 'initial' },
              minWidth: { xs: 0, md: 'auto' },
            }}
          >
            <Typography
              variant='subtitle1'
              sx={{
                color: globalColours['$global-colour-content-primary'],
                textAlign: { xs: 'left', md: 'center' },
                width: { xs: 'min-content', md: 'auto' },
                minWidth: { xs: '100%', md: 'auto' },
              }}
            >
              {heading || 'Our Spaces'}
            </Typography>
          </Box>
        </Box>

        {/* Items Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gridTemplateRows: {
              xs: 'repeat(6, fit-content(100%))',
              md: 'repeat(2, fit-content(100%))',
            },
            gap: 4,
            width: { xs: '100%', md: '1295px' },
          }}
        >
          {items.map((item, index) => (
            <GalleryCard key={index} item={item} isMobile={isMobile} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

Gallery.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string,
  items: PropTypes.array.isRequired,
};

Gallery.defaultProps = {
  className: null,
  heading: 'Our Spaces',
};

export default styled<any>(Gallery)(({ theme }) => ``);
